import React, { useContext, useEffect, useMemo } from 'react';
import { FullScreen, useFullScreenHandle } from 'react-full-screen';
import { Box, useMediaQuery } from '@mui/material';
import { Theme, useTheme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import classNames from 'classnames';
import { DocumentViewerCommonProps } from '../helper';
import { Events } from '../../../enums/Analytics';
import { FullScreenTrackingContext } from '../../Rooms/Tracking/FullScreenTrackingContext';
import Analytics from '../../../tools/Analytics';
import DocumentViewerActions from '../Actions/DocumentViewerActions';
import { getNumericSpacing } from '../../../lib/Styles';

const useStyles = makeStyles((theme: Theme) => ({
  bar: {
    alignItems: 'center',
    bottom: 0,
    display: 'flex',
    height: theme.spacing(5),
    justifyContent: 'center',
    position: 'absolute',
    width: `calc(100% - ${theme.spacing(1)})`,
  },
  root: {
    border: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(1),
    position: 'relative',
  },
  rounded: {
    borderRadius: theme.spacing(0.5),
  },
}));

export type ExcelViewerProps = DocumentViewerCommonProps;

const ExcelViewer = React.forwardRef<HTMLDivElement, ExcelViewerProps>(
  (props: ExcelViewerProps, ref) => {
    const { setFullScreenContentId } = useContext(FullScreenTrackingContext);

    const classes = useStyles();
    const theme = useTheme();
    const smDown = useMediaQuery(theme.breakpoints.down('md'));
    const fullScreenHandle = useFullScreenHandle();

    const {
      contentId,
      document,
      downloadable,
      downloadTrigger,
      onDownload,
      onLoad,
      previewDialog = false,
      rounded = false,
      trackingProperties,
      ...actionsProps
    } = props;
    const { url } = document;

    useEffect(() => {
      if (onLoad) {
        /**
         * Some notes regarding spreadsheet tracking:
         *
         * 1. Spreadsheets are tracked as single-page documents.
         *    We only display time-spent analytics
         *
         * 2. We mark this document as loaded when the component is mounted
         *    because we can't track the embedded MS viewer activity
         */
        onLoad(1);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      if (downloadable && downloadTrigger) {
        handleDownload();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [downloadable, downloadTrigger]);

    const height = useMemo(() => {
      if (fullScreenHandle.active) {
        return '100%';
      }

      // @todo support dialog resizing responsiveness
      if (previewDialog) {
        return window.innerHeight - getNumericSpacing(theme, 29);
      }

      return smDown ? 350 : 650;
    }, [fullScreenHandle.active, previewDialog, smDown, theme]);

    const handleDownload = () => {
      if (onDownload) {
        onDownload();
      }

      window.open(url, '_blank');
      Analytics.track(Events.CONTENT_DOWNLOADED, trackingProperties);
    };

    const onFullScreenChange = (isFullScreenActive: boolean) => {
      setFullScreenContentId(isFullScreenActive ? contentId : null);
    };

    return (
      // The REF must be place in the ROOT node in order to let
      // the DocumentTracker + IntersectionObserver work
      <Box
        {...{ ref }}
        className={classNames(classes.root, {
          [classes.rounded]: rounded,
        })}
      >
        <FullScreen onChange={onFullScreenChange} handle={fullScreenHandle}>
          <iframe
            height={height}
            style={{ border: 0, overflow: 'hidden' }}
            src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
              url
            )}`}
            title="Excel Viewer"
            width="100%"
          />
          <Box
            className={classes.bar}
            style={{
              background: fullScreenHandle.active ? 'black' : 'white',
              height: fullScreenHandle.active
                ? theme.spacing(3.25)
                : theme.spacing(5),
            }}
          >
            <DocumentViewerActions
              {...actionsProps}
              downloadable={downloadable}
              fullScreenHandle={fullScreenHandle}
              onDownload={handleDownload}
            />
          </Box>
        </FullScreen>
      </Box>
    );
  }
);

export default ExcelViewer;
